import React from 'react'
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer'
import moment from 'moment'

const riskColors = {
  Low: '#067647',
  Moderate: '#B7791F',
  High: '#B42318',
  Critical: '#7F1D1D',
}

const styles = StyleSheet.create({
  page: {
    padding: 34,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#172033',
    backgroundColor: '#FFFFFF',
  },
  header: {
    borderBottom: '2 solid #243B53',
    paddingBottom: 14,
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerLogo: {
    height: 34,
    maxWidth: 140,
    objectFit: 'contain',
    objectPositionX: '100%',
    objectPositionY: 0,
  },
  title: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: '#172033',
  },
  subtitle: {
    marginTop: 4,
    color: '#52606D',
    fontSize: 10,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  badge: {
    paddingVertical: 5,
    paddingHorizontal: 9,
    borderRadius: 4,
    color: '#FFFFFF',
    fontFamily: 'Helvetica-Bold',
  },
  section: {
    marginBottom: 12,
    border: '1 solid #D9E2EC',
    borderRadius: 4,
  },
  sectionHeader: {
    padding: 7,
    backgroundColor: '#F0F4F8',
    borderBottom: '1 solid #D9E2EC',
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
  },
  sectionBody: {
    padding: 8,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  item: {
    width: '50%',
    paddingRight: 12,
    marginBottom: 6,
  },
  label: {
    color: '#66788A',
    fontSize: 7,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  value: {
    fontSize: 9,
    lineHeight: 1.35,
  },
  riskRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #EEF2F7',
    paddingVertical: 6,
  },
  riskLabel: {
    width: '28%',
    fontFamily: 'Helvetica-Bold',
  },
  riskRating: {
    width: '16%',
    fontFamily: 'Helvetica-Bold',
  },
  riskNotes: {
    width: '56%',
    lineHeight: 1.35,
  },
  footer: {
    position: 'absolute',
    left: 34,
    right: 34,
    bottom: 20,
    borderTop: '1 solid #D9E2EC',
    paddingTop: 6,
    color: '#829AB1',
    fontSize: 7,
  },
})

const value = (input) => {
  if (Array.isArray(input)) return input.filter(Boolean).join(', ') || '-'
  if (input === true) return 'Yes'
  if (input === false) return 'No'
  return input || '-'
}

const fullName = (patient = {}) =>
  [patient?.demographics?.firstName, patient?.demographics?.lastName]
    .filter(Boolean)
    .join(' ') || 'Unknown Patient'

const Field = ({ label, children }) => (
  <View style={styles.item}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value(children)}</Text>
  </View>
)

const Section = ({ title, children }) => (
  <View style={styles.section}>
    <Text style={styles.sectionHeader}>{title}</Text>
    <View style={styles.sectionBody}>{children}</View>
  </View>
)

const riskSections = [
  ['Access', 'access'],
  ['Home Environment', 'homeEnvironment'],
  ['Moving and Handling', 'movingHandling'],
  ['Cognition and Communication', 'cognitionCommunication'],
  ['Clinical Care', 'clinicalCare'],
  ['Infection Control', 'infectionControl'],
  ['Safeguarding', 'safeguarding'],
  ['Transport', 'transport'],
]

const PTSRiskAssessmentPDF = ({
  assessment = {},
  patient = {},
  orgName,
  orgLogo,
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Patient Home Risk Assessment</Text>
          <Text style={styles.subtitle}>
            {orgName || 'AmbuNet'} | {assessment.assessmentType || 'Risk Assessment'}
          </Text>
          <View style={styles.badgeRow}>
            <Text
              style={[
                styles.badge,
                { backgroundColor: riskColors[assessment.overallRisk] || '#52606D' },
              ]}
            >
              Overall Risk: {assessment.overallRisk || 'Not Set'}
            </Text>
            <Text style={[styles.badge, { backgroundColor: '#243B53' }]}>
              {assessment.status || 'Completed'}
            </Text>
          </View>
        </View>
        {typeof orgLogo === 'string' && orgLogo && (
          <Image src={orgLogo} style={styles.headerLogo} />
        )}
      </View>

      <Section title="Patient and Assessment Details">
        <View style={styles.grid}>
          <Field label="Patient">{fullName(patient)}</Field>
          <Field label="Patient ID">{patient?.patientID}</Field>
          <Field label="Date of Birth">
            {patient?.demographics?.dob
              ? moment(patient.demographics.dob).format('DD/MM/YYYY')
              : ''}
          </Field>
          <Field label="Assessment Date">
            {assessment?.visitDate
              ? moment(assessment.visitDate).format('DD/MM/YYYY HH:mm')
              : ''}
          </Field>
          <Field label="Completed By">{assessment.completedByName}</Field>
          {assessment.lastUpdatedByName ? (
            <Field label="Last Updated">
              {`${assessment.lastUpdatedByName} - ${
                assessment.updatedAt
                  ? moment(assessment.updatedAt).format('DD/MM/YYYY HH:mm')
                  : ''
              }`}
            </Field>
          ) : null}
        </View>
      </Section>

      <Section title="Executive Summary">
        <Text style={styles.value}>{value(assessment.summary)}</Text>
      </Section>

      <Section title="Risk Findings">
        {riskSections.map(([label, key]) => {
          const section = assessment?.sections?.[key] || {}
          return (
            <View key={key} style={styles.riskRow}>
              <Text style={styles.riskLabel}>{label}</Text>
              <Text
                style={[
                  styles.riskRating,
                  { color: riskColors[section.rating] || '#52606D' },
                ]}
              >
                {section.rating || 'Not Assessed'}
              </Text>
              <Text style={styles.riskNotes}>{value(section.notes)}</Text>
            </View>
          )
        })}
      </Section>

      <Section title="Transport Requirements">
        <View style={styles.grid}>
          <Field label="Mobility">
            {assessment?.transportRequirements?.mobilityCategory}
          </Field>
          <Field label="Crew">
            {assessment?.transportRequirements?.crewRequirements}
          </Field>
          <Field label="Vehicle">
            {assessment?.transportRequirements?.vehicleRequirements}
          </Field>
          <Field label="Equipment">
            {assessment?.transportRequirements?.equipment}
          </Field>
          <Field label="Escort Required">
            {assessment?.transportRequirements?.escortRequired}
          </Field>
          <Field label="Oxygen Required">
            {assessment?.transportRequirements?.oxygenRequired}
          </Field>
          <Field label="Bariatric Considerations">
            {assessment?.transportRequirements?.bariatricConsiderations}
          </Field>
        </View>
      </Section>

      <Section title="Discharge Recommendations">
        <View style={styles.grid}>
          <Field label="Safe to Discharge">
            {assessment?.dischargeRecommendations?.safeToDischargeHome}
          </Field>
          <Field label="Recommended Pathway">
            {assessment?.dischargeRecommendations?.recommendedPathway}
          </Field>
          <Field label="Community Support">
            {assessment?.dischargeRecommendations?.communitySupportRequired}
          </Field>
          <Field label="Hospital Team Notes">
            {assessment?.dischargeRecommendations?.hospitalTeamNotes}
          </Field>
        </View>
      </Section>

      <Section title="Actions">
        {(assessment.actions || []).length ? (
          assessment.actions.map((action, index) => (
            <View key={`${action.action}-${index}`} style={styles.riskRow}>
              <Text style={styles.riskLabel}>{action.owner || 'Owner TBC'}</Text>
              <Text style={styles.riskNotes}>{action.action || '-'}</Text>
              <Text style={styles.riskRating}>
                {action.dueDate ? moment(action.dueDate).format('DD/MM/YYYY') : '-'}
              </Text>
            </View>
          ))
        ) : (
          <Text style={styles.value}>No actions recorded.</Text>
        )}
      </Section>

      <Text style={styles.footer}>
        Generated from AmbuNet. This assessment supports transport planning and
        hospital discharge decision-making.
      </Text>
    </Page>
  </Document>
)

export default PTSRiskAssessmentPDF
